import appLogger from "../../../infra/logger/appLogger";
const logger = appLogger.withTag("RulebookEditor");
/** Prefix of the per-owner draft keys. */
export const RULEBOOK_DRAFTS_STORAGE_KEY = "rulebook.editor.drafts.v1";
const memoryBacking = new Map();
/** In-memory drafts for an owner-less session; cleared on owner switch by the store. */
export const draftMemoryBacking = memoryBacking;
const memoryStorage = {
    getItem: (key) => memoryBacking.get(key) ?? null,
    setItem: (key, value) => {
        memoryBacking.set(key, value);
    },
    removeItem: (key) => {
        memoryBacking.delete(key);
    },
};
/** Where `ownerId`'s drafts live: the browser's storage for a signed-in owner, memory otherwise. */
export function draftStorageForOwner(ownerId) {
    // eslint-disable-next-line no-restricted-syntax -- clientStorage is async; drafts need a synchronous store for useSyncExternalStore.
    if (ownerId !== null && typeof localStorage !== "undefined") {
        // eslint-disable-next-line no-restricted-syntax -- same synchronous-storage requirement as above.
        return localStorage;
    }
    return memoryStorage;
}
export const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
export const isRulebookDraft = (value) => {
    if (!isRecord(value)) {
        return false;
    }
    const definition = value["definition"];
    return isRecord(definition) && typeof definition["id"] === "string" && typeof value["updated_at"] === "string";
};
/**
 * Repair a stored draft's shape so hand-edited or older payloads can't break the Editor: strings and
 * arrays are coerced rather than trusted. The definition carries no `tags`; draft `meta.tags` gets the
 * same string-array coercion.
 */
export const normalizeRulebookDraft = (draft) => {
    const definition = draft.definition;
    const metaTags = draft.meta?.tags;
    const definitionWithoutDescription = { ...definition };
    delete definitionWithoutDescription["description"];
    const draftWithoutInvalidLink = { ...draft };
    if (typeof draftWithoutInvalidLink["rulebook_id"] !== "string") {
        delete draftWithoutInvalidLink["rulebook_id"];
    }
    return {
        ...draftWithoutInvalidLink,
        definition: {
            ...definitionWithoutDescription,
            name: typeof definition["name"] === "string" ? definition["name"] : "",
            ...(typeof definition["description"] === "string" ? { description: definition["description"] } : {}),
            meters: Array.isArray(draft.definition.meters) ? draft.definition.meters : [],
            combined_rules: Array.isArray(draft.definition.combined_rules) ? draft.definition.combined_rules : [],
        },
        ...(draft.meta
            ? { meta: { ...draft.meta, tags: Array.isArray(metaTags) ? metaTags.filter((tag) => typeof tag === "string") : [] } }
            : {}),
    };
};
const UUID_HEX_BASE = 16;
const UUID_VARIANT_MASK = 0x3;
const UUID_VARIANT_BITS = 0x8;
/** A v4 UUID; `crypto.randomUUID` where available, else a Math.random one (uniqueness per draft is all it needs). */
export const newDraftPublishKey = () => {
    if (typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * UUID_HEX_BASE);
        return (char === "x" ? random : (random & UUID_VARIANT_MASK) | UUID_VARIANT_BITS).toString(UUID_HEX_BASE);
    });
};
/** The `<id>-copy`, `-copy-2`, `-copy-3`, … id a duplicate lands on. */
export const nextDraftCopyId = (id, existingIds) => {
    let candidate = id + "-copy";
    let counter = 2;
    while (candidate in existingIds) {
        candidate = id + "-copy-" + counter;
        counter += 1;
    }
    return candidate;
};
/** Where a whole-map corruption is preserved before the next write replaces it (`<draftsKey>.corrupt`). */
export const corruptDraftsBackupKey = (draftsKey) => `${draftsKey}.corrupt`;
/** Preserve the unreadable raw payload under a sibling key (once) so a rewrite doesn't destroy it. */
export const backupCorruptDrafts = ({ storage, draftsKey }) => {
    const backupKey = corruptDraftsBackupKey(draftsKey);
    if (storage.getItem(backupKey) !== null) {
        return;
    }
    const raw = storage.getItem(draftsKey);
    if (raw === null) {
        return;
    }
    try {
        storage.setItem(backupKey, raw);
        logger.warn("Corrupt rulebook drafts were preserved under " + backupKey);
    }
    catch (error) {
        logger.warn("Could not preserve corrupt rulebook drafts under " + backupKey, error);
    }
};
